/** Derive Robinhood margin summary fields from get_portfolio snapshot data. */

export interface RobinhoodMarginSummary {
  marginUsed: number;
  marginAvailable: number;
  marginLimit: number;
  marginBufferPct: number | null;
  annualRatePct: number | null;
  dailyInterest: number | null;
}

/** First $1K of settled margin balance is interest-free on Robinhood Gold. */
export const ROBINHOOD_GOLD_INTEREST_FREE_USD = 1_000;

function parseAmount(value: unknown): number | null {
  const num =
    typeof value === "string" ? Number(value) : typeof value === "number" ? value : Number.NaN;
  return Number.isFinite(num) ? num : null;
}

function firstAmount(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = parseAmount(obj[key]);
    if (value != null) return value;
  }
  return null;
}

/** Robinhood margin APR tier by settled debit balance (Dec 2025 table). */
export function robinhoodMarginAprPct(settledDebitBalance: number): number | null {
  if (!Number.isFinite(settledDebitBalance) || settledDebitBalance <= 0) return null;
  if (settledDebitBalance <= 50_000) return 5;
  if (settledDebitBalance <= 100_000) return 4.8;
  if (settledDebitBalance <= 1_000_000) return 4.5;
  if (settledDebitBalance <= 10_000_000) return 4.25;
  if (settledDebitBalance <= 50_000_000) return 4.2;
  return 3.95;
}

function estimateSettledDebitBalance(
  marginUsed: number,
  portfolioValue: number | null,
  unleveragedBuyingPower: number | null,
): number {
  if (
    portfolioValue != null &&
    portfolioValue > 0 &&
    unleveragedBuyingPower != null &&
    unleveragedBuyingPower >= 0 &&
    unleveragedBuyingPower < portfolioValue
  ) {
    // RH accrues daily interest on prior-day settled debit, which is often below
    // real-time margin used while trades are settling.
    return marginUsed * ((portfolioValue - unleveragedBuyingPower) / portfolioValue);
  }
  return marginUsed;
}

function deriveMaintenanceRequirement(
  portfolio: Record<string, unknown>,
  buyingPowerObj: Record<string, unknown> | null,
  portfolioValue: number | null,
  buyingPower: number,
): number | null {
  const explicit =
    firstAmount(portfolio, [
      "total_maintenance_requirement",
      "maintenance_requirement",
      "maintenance",
      "total_maintenance",
    ]) ??
    (buyingPowerObj
      ? firstAmount(buyingPowerObj, [
          "total_maintenance_requirement",
          "maintenance_requirement",
          "maintenance",
        ])
      : null);
  if (explicit != null && explicit >= 0) return explicit;

  // MCP snapshots omit maintenance. On heavily margined accounts, buying power
  // tracks cushion above maintenance; using it as a maintenance proxy yields
  // buffer ≈ (portfolio value − buying power) / portfolio value.
  if (portfolioValue != null && portfolioValue > 0 && buyingPower >= 0 && buyingPower < portfolioValue) {
    return buyingPower;
  }
  return null;
}

function deriveMarginBufferPct(
  portfolio: Record<string, unknown>,
  buyingPowerObj: Record<string, unknown> | null,
  portfolioValue: number | null,
  maintenanceRequirement: number | null,
): number | null {
  const explicitPct = firstAmount(portfolio, [
    "margin_buffer_pct",
    "buffer_pct",
    "margin_buffer_percent",
  ]);
  if (explicitPct != null) return explicitPct;

  const explicitBuffer =
    firstAmount(portfolio, ["margin_buffer", "buffer", "excess_maintenance"]) ??
    (buyingPowerObj
      ? firstAmount(buyingPowerObj, ["margin_buffer", "buffer", "excess_maintenance"])
      : null);

  if (portfolioValue != null && portfolioValue > 0) {
    if (explicitBuffer != null) {
      return (explicitBuffer / portfolioValue) * 100;
    }
    if (maintenanceRequirement != null && maintenanceRequirement >= 0) {
      const bufferAmount = portfolioValue - maintenanceRequirement;
      if (bufferAmount >= 0) {
        return (bufferAmount / portfolioValue) * 100;
      }
    }
  }
  return null;
}

function deriveSettledDebitBalance(
  portfolio: Record<string, unknown>,
  marginUsed: number,
  portfolioValue: number | null,
  unleveragedBuyingPower: number | null,
): number {
  const explicit = firstAmount(portfolio, [
    "settled_debit_balance",
    "settled_margin_balance",
    "adjusted_debit_balance",
    "settled_margin",
    "previous_settled_debit_balance",
  ]);
  if (explicit != null && explicit >= 0) return explicit;
  return estimateSettledDebitBalance(marginUsed, portfolioValue, unleveragedBuyingPower);
}

export function deriveRobinhoodMarginSummary(
  portfolioRaw: unknown,
  opts: { isMarginAccount?: boolean; assumeGoldInterestFreeTier?: boolean } = {},
): RobinhoodMarginSummary | null {
  if (!portfolioRaw || typeof portfolioRaw !== "object" || Array.isArray(portfolioRaw)) {
    return null;
  }
  const portfolio = portfolioRaw as Record<string, unknown>;
  const cash = parseAmount(portfolio.cash) ?? 0;
  const totalValue = parseAmount(portfolio.total_value);
  const buyingPowerObj =
    portfolio.buying_power &&
    typeof portfolio.buying_power === "object" &&
    !Array.isArray(portfolio.buying_power)
      ? (portfolio.buying_power as Record<string, unknown>)
      : null;
  const buyingPower = parseAmount(buyingPowerObj?.buying_power) ?? 0;
  const unleveragedBuyingPower = parseAmount(buyingPowerObj?.unleveraged_buying_power);

  const marginUsed = cash < 0 ? Math.abs(cash) : 0;
  const isMarginAccount =
    opts.isMarginAccount === true ||
    marginUsed > 0 ||
    (unleveragedBuyingPower != null && buyingPower > unleveragedBuyingPower + 0.01);
  if (!isMarginAccount) return null;

  const marginAvailable = Math.max(0, buyingPower);
  const marginLimit = marginUsed > 0 ? marginUsed + marginAvailable : marginAvailable;

  const maintenanceRequirement = deriveMaintenanceRequirement(
    portfolio,
    buyingPowerObj,
    totalValue,
    buyingPower,
  );
  const marginBufferPct = deriveMarginBufferPct(
    portfolio,
    buyingPowerObj,
    totalValue,
    maintenanceRequirement,
  );

  const assumeGold = opts.assumeGoldInterestFreeTier !== false;
  const settledDebit = deriveSettledDebitBalance(
    portfolio,
    marginUsed,
    totalValue,
    unleveragedBuyingPower,
  );
  const annualRatePct = marginUsed > 0 ? robinhoodMarginAprPct(settledDebit) : null;
  const interestSubject =
    marginUsed > 0 && annualRatePct != null
      ? Math.max(
          0,
          settledDebit - (assumeGold ? ROBINHOOD_GOLD_INTEREST_FREE_USD : 0),
        )
      : null;
  const dailyInterest =
    interestSubject != null && annualRatePct != null
      ? (interestSubject * (annualRatePct / 100)) / 360
      : null;

  if (marginUsed <= 0 && marginLimit <= 0) return null;

  return {
    marginUsed,
    marginAvailable,
    marginLimit,
    marginBufferPct,
    annualRatePct,
    dailyInterest,
  };
}

export function formatMarginPercent(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}%`;
}

export function formatMarginRate(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}%`;
}
